import React, { useState, useEffect, useReducer, useRef, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  Grid,
  Box,
  Card,
  Stack,
  Typography,
  IconButton,
} from "@mui/material";
import {
  Tabs,
  Tab,
  Input,
  InputAdornment,
  Tooltip,
  Button,
} from "@mui/material";
import dayjs from "dayjs";
import { get } from "aws-amplify/api";
import EditEndDateDialog from "./EditEndDateDialog";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import {
  DataGrid,
  GridToolbarContainer,
  GridActionsCellItem,
  GridToolbarColumnsButton,
  GridRowModes,
  useGridApiRef,
} from "@mui/x-data-grid";
import { I18n } from "@aws-amplify/core";
import ConfirmDeleteDialog from "popup/ConfirmDeleteDialog";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import currentUserToken from "CurrentUserToken";
import SearchIcon from "@mui/icons-material/Search";
import { useTheme } from "@mui/material/styles";
import { post } from "aws-amplify/api";
import { APIStatusDialog } from "components/APIStatusDialog";
import { DataGridPro } from "@mui/x-data-grid-pro/DataGridPro";
import { itIT } from "@mui/x-data-grid/locales";
import { deDE } from "@mui/x-data-grid/locales";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);
const reducer = (lastState, action) => {
  if (action.type === "DATA_RECEIVED") {
    const audit = action.value;
    if (Array.isArray(audit)) {
      audit.forEach((element) => {
        if (typeof element.timestamp === "number") {
          element.timestamp = dayjs(element.timestamp).format(
            "YYYY-MM-DD HH:mm"
          );
        }
      });
      return { audit: audit, fetched: true };
    }
  }
  return lastState;
};
const LockDialog = ({ open, onClose, language, isGuest}) => {
  const [state, dispatch] = useReducer(reducer, {
    audit: [],
    fetched: false,
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [apiStatus, setApiStatus] = useState({
    open: false,
    status: "",
    message: "",
  });
  const [tableParentIsMD, setTableParentIsMD] = useState(false);
  const [startDate, setStartDate] = useState(
    dayjs().utc().subtract(1, "month").startOf("day")
  );
  const [endDate, setEndDate] = useState(dayjs().utc().endOf("day"));
  const [resizeThrottle, setResizeThrottle] = useState(60);
  const toolbarRef = useRef(null);
  useEffect(() => {
    const parentElement = toolbarRef.current;
    if (!parentElement) {
      console.error("toolbarRef is not correctly attached to any DOM element.");
      return;
    }
    const checkSize = (width) => {
      const breakpoint = 960; // 960px is md
      setTableParentIsMD(width < breakpoint);
    };
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        checkSize(entry.contentRect.width);
      }
    });
    resizeObserver.observe(parentElement);
    checkSize(parentElement.offsetWidth);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  const apiRef = useGridApiRef();
  const [searchText, setSearchText] = useState("");
  const theme = useTheme();
  const [endDateTime, setEndDateTime] = useState("");
  const [rowModesModel, setRowModesModel] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [filters, setFilters] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedRowDelet, setSelectedRowDelet] = useState(null);
  const handleOpenEditDialog = (row) => {
    setSelectedRow(row);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
  };
  const handleRowSelection = (ids) => {
    setSelectedRows(ids);
  };
  const handleColumnVisibilityChange = (newModel) => {
    const hiddenColumns = Object.keys(newModel).filter(
      (field) => !newModel[field]
    );
    localStorage.setItem("hiddenColumns", JSON.stringify(hiddenColumns));
  };
  const tlmGetAlarmLockSuspension = async () => {
    try {
      const restOperation = get({
        apiName: "TelemonitoringAPI",
        path: "/alarm/suspension/lock/get",
        options: {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
          queryParams: {},
        },
      });
      const { body } = await restOperation.response;
      const response = await body.json();

      dispatch({ type: "DATA_RECEIVED", value: response });
    } catch (error) {
      console.error("Errore durante la chiamata API:", error);
    }
  };
  useEffect(() => {
    if (open) {
      tlmGetAlarmLockSuspension();
    }
  }, [open]);

  const handleDeleteClick = (row) => {
    setSelectedRowDelet(row); // Set the selected row to delete
    handleConfirmDelete(row.id); // Pass the row ID directly for deletion
  };

  useEffect(() => {
    setSearchText(""); // Reset search text when the dialog is closed
  }, [onClose]);

  const handleConfirmDelete = async (id) => {
    try {
      setApiStatus({
        open: true,
        status: "pending",
        message: I18n.get("Deleting the alarm lock suspension"),
      });

      const restOperation = await tlmDeletePI(id);
      const response = await restOperation.response;
      if (response.statusCode === 200) {
        setApiStatus({
          open: true,
          status: "success",
          message: I18n.get("Alarm lock suspension successfully deleted"),
        });
        dispatch({
          type: "DATA_RECEIVED",
          value: state.audit.filter((row) => row.alarm_type_id !== id),
        });
        tlmGetAlarmLockSuspension();
      } else {
        setApiStatus({
          open: true,
          status: "error",
          message: I18n.get("Failed to delete alarm lock suspension"),
        });
      }

      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 2000);
    } catch (error) {
      setApiStatus({
        open: true,
        status: "error",
        message: I18n.get("Failed to delete alarm lock suspension"),
      });
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 2000);
    }
  };

  const tlmDeletePI = async (id) => {
    try {
      const restOperation = get({
        apiName: "TelemonitoringAPI",
        path: "/alarm/suspension/lock/delete",
        options: {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
          queryParams: {
            id: id,
          },
        },
      });
      return restOperation;
    } catch (error) {
      throw error;
    }
  };

  const columns = useMemo(() => {
    return [
      {
        field: "executor",
        headerName: I18n.get("Executor"),
        width: 200,
      },
      {
        field: "original_equipment",
        headerName: I18n.get("Original Equipment"),
        width: 200,
      },
      {
        field: "calendar_name",
        headerName: I18n.get("Calendar name"),
        width: 200,
      },
      {
        field: "datetime_start",
        headerName: I18n.get("Start Date"),
        width: 200,
        valueFormatter: (value) => {
          if (!value) {
            return ""; // Handle null or undefined values
          }
          const formattedDate = dayjs
            .utc(value)
            .tz(dayjs.tz.guess())
            .format("YYYY-MM-DD HH:mm:ss");
          return formattedDate;
        },
      },
      {
        field: "datetime_end",
        headerName: I18n.get("End Date"),
        width: 200,
        editable: true,
        valueFormatter: (value) => {
          if (!value) {
            return ""; // Handle null or undefined values
          }
          const formattedDate = dayjs
            .utc(value)
            .tz(dayjs.tz.guess())
            .format("YYYY-MM-DD HH:mm:ss");
          return formattedDate;
        },
      },
      {
        field: "original_store",
        headerName: I18n.get("Original Store"),
        width: 200,
      },
      {
        field: "process_note",
        headerName: I18n.get("Process Note"),
        width: 200,
      },
      {
        field: "alarm_text",
        headerName: I18n.get("Alarm Text"),
        width: 200,
      },
      {
        field: "actions",
        headerName: I18n.get("Actions"),
        width: 100,
        renderCell: (params) => (
          <Box direction="row" spacing={1}>
            <IconButton onClick={() => handleOpenEditDialog(params.row)} disabled={isGuest}>
              <EditIcon />
            </IconButton>
            <IconButton onClick={() => handleDeleteClick(params.row)} disabled={isGuest}>
              <DeleteIcon />
            </IconButton>
          </Box>
        ),
      },
    ];
  }, [rowModesModel, isGuest]);

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false); // Chiudi il dialogo senza fare nulla
  };
  const handleSaveEndDate = async (alarmTypeId, newEndDate) => {
    try {
      setApiStatus({
        open: true,
        status: "pending",
        message: I18n.get("Modifying"),
      });
      const restOperation = await tlmModifyDescription(alarmTypeId, newEndDate); // Pass the formatted date
      const response = await restOperation.response;
      if (response.statusCode === 200) {
        setApiStatus({
          open: true,
          status: "success",
          message: I18n.get("Modification successfully saved"),
        });
        dispatch({ type: "DATA_RECEIVED", value: response });
        tlmGetAlarmLockSuspension();
        setTimeout(() => {
          setApiStatus((prevState) => ({ ...prevState, open: false }));
        }, 2000);
        return;
      } else {
        setApiStatus({
          open: true,
          status: "error",
          message: I18n.get("Failed to save modification"),
        });
        setTimeout(() => {
          setApiStatus((prevState) => ({ ...prevState, open: false }));
        }, 2000);
      }
    } catch (error) {
      setApiStatus({
        open: true,
        status: "error",
        message: I18n.get("Note Pattern Failed to save modification"),
      });
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 2000);
    }
  };

  const tlmModifyDescription = async (alarmTypeId, endDateRow) => {
    try {
      const restOperation = await post({
        apiName: "TelemonitoringAPI",
        path: "/alarm/suspension/lock/edit",
        options: {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
          queryParams: {
            id: alarmTypeId,
            end_date: endDateRow,
          },
        },
      });
      return restOperation;
    } catch (error) {
      console.error("Error during API call:", error);
    }
  };

  const applyStoreFiltering = (value) => {
    let matchesAllFilters = true;
    if (searchText) {
      const searchTextLower = searchText.toLowerCase();
      matchesAllFilters =
        matchesAllFilters &&
        Object.values(value).some((val) =>
          String(val).toLowerCase().includes(searchTextLower)
        );
    }
    return matchesAllFilters;
  };
  const filteredRows = state.audit.filter(applyStoreFiltering);
  const filteredRowsCount = filteredRows.length;
  const searchChanged = (event) => {
    setSearchText(event.target.value);
  };
  const renderToolbar = () => {
    return (
      <div ref={toolbarRef}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h5" mt={0.5} mb={1} sx={{ pb: 2 }}>
            {I18n.get("Active Lock Rules")} (
            {filters ? filteredRowsCount : totalCount})
          </Typography>
        </Stack>
        <Stack
          direction={tableParentIsMD ? "column" : "row"}
          alignItems="center"
          justifyContent={tableParentIsMD ? "center" : "space-between"}
          spacing={2}
          sx={{ pb: 2 }}
        >
          <Stack
            direction={tableParentIsMD ? "column" : "row"}
            spacing={2}
            sx={{ width: "100%" }}
          >
            <Box
              sx={{
                ml: 2,
                flexGrow: 1,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Input
                type="text"
                placeholder={I18n.get("Search")}
                onChange={searchChanged}
                fullWidth={tableParentIsMD}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                }
              />
            </Box>
          </Stack>
        </Stack>
      </div>
    );
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xxl">
      <DialogContent>
        <Box sx={{ p: 2 }}>
          <Card sx={{ p: 3 , height:"100%"}}>
            {renderToolbar()}
            <Box sx={{flexGrow:1}}>
              <DataGridPro
                sx={{ minHeight: "40vh", height:"100%"}}
                localeText={
                  language === "it"
                    ? itIT.components.MuiDataGrid.defaultProps.localeText
                    : language === "ge"
                    ? deDE.components.MuiDataGrid.defaultProps.localeText
                    : ""
                }
                onCellDoubleClick={(params, event) => {
                  event.defaultMuiPrevented = true;
                }}
                editMode="row"
                rows={filteredRows}
                columns={columns}
                disableRowSelectionOnClick
                getRowId={(row) => row.id}
                rowModesModel={rowModesModel}
                onRowModesModelChange={(newModel) => setRowModesModel(newModel)}
                onRowSelectionModelChange={(newSelection) =>
                  handleRowSelection(newSelection)
                }
                initialState={{
                  density: "compact",
                  pinnedColumns: { right: ["actions"] },
                }}
                apiRef={apiRef}
                resizeThrottleMs={resizeThrottle}
                loading={!state.fetched || resizeThrottle > 60}
                onProcessRowUpdateError={(error) => console.log({ error })}
                onColumnVisibilityModelChange={handleColumnVisibilityChange}
                pagination
                autoPageSize
                slotProps={{
                  loadingOverlay: {
                    variant: "skeleton",
                    noRowsVariant: "skeleton",
                  },
                }}
              />
            </Box>
          </Card>
        </Box>

        <APIStatusDialog
          open={apiStatus.open}
          status={apiStatus.status}
          message={apiStatus.message}
        />

        <EditEndDateDialog
          open={editDialogOpen}
          onClose={handleCloseEditDialog}
          row={selectedRow}
          onSave={handleSaveEndDate}
        />
      </DialogContent>
    </Dialog>
  );
};

export default LockDialog;
